import theme from '../../theme'
import { IProps, MessageResponse } from './types'
import Navbar from '../Navbar'
import ChatList from '../ChatList'
import ChatInput from '../ChatInput'
import React, { useEffect, useRef, useState } from 'react'
import { useStyles } from '../../style/styles'
import { Box, Button, Grid, IconButton, ThemeProvider, Typography } from '@mui/material'
import * as utils from '../../lib/messageUtils'
import { useDispatch, useSelector } from 'react-redux'
import { ChannelData, ChannelInfo, RootState } from '../../redux/channel/types'
import { setChannelData,updateChannelMessageList,updateTermsAgreed } from '../../redux/channel/actions'
import messagingService from '../../services/MessagingService'
import { MessagingConstants } from '../../lib/enums'
import TermsOfService from '../TermsOfService'
import getDisplayName from '../../lib/getDisplayName'
import Timezone from '../Timezone'
import TermsOfServiceNotification from '../TermsOfServiceNotification'
import { logInfo, logError } from '../../lib/rumLogger'
import Notifications from '../Notifications'
import CloseIcon from '@mui/icons-material/Close';

const Chat = (props: IProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { sender, receiver, channel, job, connections, token, onMinimizeChat, websocketConnection, origin, channelMetadata} = props
  const [isEndedConversation, setIsEndedConversation] = useState(false)
  const channelsReducer = useSelector((state: RootState) => state.channels.channels)
  const loggedInUserArn = sender.userArn
  const { channelName } = channel
  const [shouldLoadMessages, setShouldLoadMessages] = useState(false)
  const [channelArn, setChannelArn] = useState<string | null>(null)
  const [channelData, setChannelReducer] = useState<ChannelData>()
  const [termsOfServiceAgreed, setTermsOfServiceAgreed] = useState(true)
  const [inputFocused, setInputFocused] = useState(false);
  const [hideSubjectLine, setHideSubjectLine] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(sender?.timezone);
  const [endOrReenableActionDoer, setEndOrReenableActionDoer] = useState<string | null>(null)
  const [adminID, setAdminId] = useState<string>("")
  const [updatedChannel, setUpdatedChannel] = useState<any>(channel)
  const [updatedConnections, setUpdatedConnections] = useState<any>(connections)
  const [showChat, setShowChat] = useState<boolean>(true)
  const [isLastMessageSender, setIsLastMessageSender] = useState<boolean>(true)
  const [hasReadLastMessage, setHasReadLastMessage] = useState(true)
  const [initialNextToken, setInitialNextToken] = useState<string | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const [showOptInBanner, setShowOptInBanner] = useState(false);

  useEffect(() => {
    if (!sender || !sender.userArn || sender.role?.toLowerCase() !== "jobseeker") return;

    const fetchNumbers = async () => {
      const data = await utils.fetchJobseekerNumbers(sender.userArn);

      const formatted = data.map((item: any) => ({
        id: item.PhoneID,
        number: item.PhoneNumber.Number,
        countryCode: item.PhoneNumber.CountryCode,
        type: item.PhoneNumber.Type,
        isOptedIn: item.SmsOptIn,
      }));

      const noPhones = formatted.length === 0;
      const noneOptedIn =
        formatted.length > 0 &&
        formatted.every((p: { isOptedIn: any }) => !p.isOptedIn);

      setShowOptInBanner(noPhones || noneOptedIn);
    };

    fetchNumbers();
  }, [sender?.userArn, sender?.role]);


  /* TERMS OF SERVICE NOTIFICATION: to track height of component */
  useEffect(() => {
    if (componentRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          document.documentElement.style.setProperty('--default-terms-height', `${height}px`);
        }
      });
      resizeObserver.observe(componentRef.current);
      return () => resizeObserver.disconnect();
    }
  });

  const handleTimezoneChange = (timezone: string ) => {
    setSelectedTimezone(timezone)
  }

  useEffect(() => {
    if(channel?.metadata){
      setAdminId(JSON.parse(channel.metadata).Admin)
    }
  }, [channel?.metadata])
 

  useEffect(() => {
    setTermsOfServiceAgreed(sender.termsAgreed);
  }, [sender.termsAgreed])

  // useEffect(() => {
  //   const showChat = sender.role == "Jobseeker" || (sender.role == "Recruiter" && termsOfServiceAgreed )
  //   setShowChat(showChat)
  // }, [sender])

  const handleTermsAction = (agreedToTerms: boolean) => {
    setTermsOfServiceAgreed(agreedToTerms)
    if (agreedToTerms) {
      utils.updateTermsOfServiceAgreed(sender.userArn)
      if(channelsReducer[channelName]?.userInfo) {
        dispatch(updateTermsAgreed(channelName, agreedToTerms))
      }
    }
  }

  const [isTimezoneDialogOpen, setTimezoneDialogOpen] = useState(false);
  const handleOpenTimezoneDialog = () => {
    setTimezoneDialogOpen(true);
  };

  const handleCloseTimezoneDialog = () => {
    setTimezoneDialogOpen(false);
  };

  const [isNotificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const handleOpenNotificationsDialog = () => {
    setNotificationsDialogOpen(true);
  };

  const handleCloseNotificationsDialog = () => {
    setNotificationsDialogOpen(false);
  };

  useEffect(() => {
    if(!channelsReducer[channelName] || channelsReducer[channelName]?.channelArn) {
      fetchChannel(channelsReducer[channelName]?.channelArn).then((returnedChannel: any) => {
        setChannelInfo(returnedChannel)
      }).catch(err => { throw err })
    }
  }, [channelName, sender])

  useEffect(() => {
    setChannelReducer(channelsReducer[channelName])
    if (shouldLoadMessages) {
      handleLoadMessages()
      setShouldLoadMessages(false)
    }
  }, [channelsReducer, shouldLoadMessages])

  useEffect(() => {
    messagingService.subscribeToMessageUpdate(callbackProcessor)
    return () => {
      messagingService.unsubscribeFromMessageUpdate(callbackProcessor)
    }
  }, [])

  const fetchChannel = async (channelArn: string) => {
    const channelToFetch = channel?.channelArn ? channel.channelArn : channelArn
    const channelDetails = await utils.getChannelMembershipDetails(channelToFetch, sender.userArn)
    const returnedChannel = channelDetails?.ChannelMembership

    /* TJSKR-2399 - update read marker if user hasn't read last message yet */
    const userHasReadLastMessage = (utils.compareTimestamp(returnedChannel?.ChannelSummary?.LastMessageTimestamp, returnedChannel?.AppInstanceUserMembershipSummary?.ReadMarkerTimestamp))
    setHasReadLastMessage(userHasReadLastMessage)

    if(returnedChannel) {
      const channelMetadata = JSON.parse(returnedChannel?.ChannelSummary?.Metadata)
      const adminUserArn = sender.role.toUpperCase() === MessagingConstants.RECRUITER ? sender.userArn : receiver.userArn
      const endConversationTimestamp = channelMetadata?.EndConversationTimestamp || null
      const isEndedConversation = endConversationTimestamp ? true : false
      const actionDoer = channelMetadata?.EndOrReenableActionDoer
      setUpdatedChannel(returnedChannel)
      setChannelArn(returnedChannel?.ChannelSummary?.ChannelArn)
      setIsEndedConversation(isEndedConversation)
      setEndOrReenableActionDoer(actionDoer)

      const blockedBy = channelMetadata?.BlockedBy
      if (blockedBy && blockedBy.trim() !== '') {
        if (blockedBy == loggedInUserArn) {
          setUpdatedConnections({ isBlocked: false, isBlockedLink: true})
        } else {
          setUpdatedConnections({ isBlocked: true, isBlockedLink: false})
        }
      } else {
        setUpdatedConnections({ isBlocked: connections.isBlocked, isBlockedLink: connections.isBlockedLink})
      }

      return returnedChannel
    } return null
  }

  const callbackProcessor = async(message: MessageResponse) => {
    const messageType = message?.headers['x-amz-chime-event-type']
    const record = JSON.parse(message?.payload)
    const isRecordNotEmpty = record && record?.Content
    switch(messageType) {
      case 'CREATE_CHANNEL_MESSAGE':
          // if (record.Type === 'CONTROL' || record.Persistence === 'NON_PERSISTENT') {
          //   return
          // }
        if(isRecordNotEmpty) {
          /* TJSKR-3383: Send a post message to JS app for new messages incoming from jobseeker */
          if(record?.Sender.Arn !== sender?.userArn && sender?.role === 'Jobseeker' && record.Persistence != "NON_PERSISTENT") {
            console.log('Sending a message to JS App')
            window.parent.postMessage({
              type: 'HANDLE_TIMESTAMP_UPDATE',
              channel_arn: record?.ChannelArn,
              jobseeker_id: sender?.external_user_id,
              messaging_user_id: sender?.userArn,
              last_message_timestamp: record?.CreatedTimestamp,
              total_unread_messages: 0
            }, '*')
          }
          if(utils.checkIfBlockIndicatorMessage(record.Content)) {
            fetchChannel(record?.ChannelArn)
          }
          /* If logged in user is the latest message-sender */
          else if (sender?.userArn ===  utils.splitUserArn(record?.Sender?.Arn)) {
            if(sender?.role.toLowerCase() === 'recruiter') {
                setHasReadLastMessage(prev => !prev)
              } else {
                setHasReadLastMessage(true)
              }
          }
          /* If logged in user is NOT the latest message-sender */
          else if(sender?.userArn !== utils.splitUserArn(record?.Sender?.Arn)) {
            if(channel?.channelArn === record?.ChannelArn) {
              utils.updateReadMarker(sender?.userArn, record?.ChannelArn, false, receiver)
              if(sender?.role.toLowerCase() === 'recruiter') {
                setHasReadLastMessage(prev => !prev)
              } else {
                setHasReadLastMessage(true)
              }
            }
          }
          handleLoadMessages()
          setShouldLoadMessages(true)
        }
        break
      case 'CREATE_CHANNEL_MEMBERSHIP':
        fetchChannel(record?.ChannelArn)
        handleLoadMessages()
        setShouldLoadMessages(true)
        break;
      case 'UPDATE_CHANNEL':
        fetchChannel(record?.ChannelArn)
        handleLoadMessages()
        setShouldLoadMessages(true)
        break
      case 'REDACT_CHANNEL_MESSAGE':
      case 'UPDATE_CHANNEL_MESSAGE':
        fetchChannel(record?.ChannelArn)
        handleLoadMessages()
        setShouldLoadMessages(true)
        break
      case 'SESSION_ESTABLISHED':
        let receiverData = { externalUserId: receiver.external_user_id, messagingId: receiver.userArn, role: receiver.role }
        const senderData = { externalUserId: sender.external_user_id, messagingId: sender.userArn, role: sender.role }
        if (channel.channelArn) {
          utils.sendChannelMessage(
            senderData,
            receiverData,
            channel.channelArn,
            '',
            '',
            true
          );
        }
        break
    }
  }

  const setChannelInfo = async (channel: any) => {
    if(channel && channel.ChannelSummary) {
      const { ChannelSummary } = channel ? channel : null
      const { ChannelArn } = ChannelSummary ? ChannelSummary : null
      if(ChannelArn) {
        const listMessages = await utils.listChannelMessages(ChannelArn, loggedInUserArn)
        const messages = listMessages && listMessages.Messages ? listMessages.Messages : null
        setInitialNextToken(listMessages?.NextToken || null)

        const channelData: any = {
          [channelName]: {
            channelArn: ChannelArn,
            totalUnreadCount: 0,
            messageList: messages,
            userInfo: sender
          }
        }
        setChannelReducer(channelData)
        dispatch(setChannelData(channelData))
      }
      else {
        const channelData: any = {
          [channelName]: {
            channelArn: null,
            totalUnreadCount: 0,
            messageList: [],
            userInfo: sender
          }
        }
        setChannelReducer(channelData)
        dispatch(setChannelData(channelData))
      }
    }
  }

  const handleLoadMessages = async () => {
    if (channelData && channelData?.channelArn) {
      try {
        const listMessages = await utils.listChannelMessages(channelData.channelArn, loggedInUserArn)
        const messages = listMessages?.Messages || null
        setInitialNextToken(listMessages?.NextToken || null)

        if (messages) {
          const updatedChannelsReducer = { ...channelsReducer }
          const updatedMessageList = [...messages]
          updatedChannelsReducer[channelName] = {
            ...updatedChannelsReducer[channelName],
            messageList: updatedMessageList
          }
          dispatch(setChannelData(updatedChannelsReducer))
        }
      } catch (error) {
        logError(error as Error, 'handleLoadMessages', { channelArn: channelData?.channelArn })
      }
    }
  };

  const handleEndConversation = async() => {
    if(channelArn) {
      const actionMarker = isEndedConversation ? MessagingConstants.REENABLE_CONVERSATION_MARKER : MessagingConstants.END_CONVERSATION_MARKER
      if (loggedInUserArn) {
        const res = await utils.updateChannelMarkerBackend(
          channelArn,
          loggedInUserArn,
          adminID,
          actionMarker,
          null,
          receiver)
        setIsEndedConversation(!isEndedConversation)
        setEndOrReenableActionDoer(loggedInUserArn)
        handleLoadMessages()
      }
    }
  }

  const handleInputFocusChange = (focused: boolean) => {
    setInputFocused(focused)
    /* TJSKR-1721: Hide subject line on focus only if origin.isMobile is true (iframe was called from mobile device) */
    if (origin?.isMobile) {
      setHideSubjectLine(focused)
    }
  }

  return (loggedInUserArn) ? (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700"/>
      <ThemeProvider theme={theme}>
      <div className={classes.mainContainer} >
        <Grid container direction='column' >
          { showChat ? (
            <>
            <Grid item>
            <Navbar
              receiver={receiver}
              userType={sender.role.toUpperCase()}
              username={getDisplayName(receiver, termsOfServiceAgreed)}
              onEndConversation={handleEndConversation}
              isEndedConversation={isEndedConversation}
              token={token}
              connections={updatedConnections}
              platform='mobile'
              onTimezoneClick={handleOpenTimezoneDialog}
              onNotificationClick={handleOpenNotificationsDialog}
              userArn = {sender.userArn}
              endOrReenableActionDoer={endOrReenableActionDoer}
              onMinimizeChat={onMinimizeChat}
              job={job}
              channelMetadata={channel?.metadata}
              websocketConnection={websocketConnection}
            />
          </Grid>
          <Grid item>
            <div className={classes.chatBody}>
              {!hideSubjectLine && (
                <Grid
                  item
                  container
                  alignContent='center'
                  className={classes.mobileChatDescriptionContainer}>
                  <Typography className={classes.mobileChatDescription}>
                    {/* Subject: Your Application @ {job.company} for {job.position} */}
                    <span className={classes.multiLine}>
                      Subject: Your Application @ {job.company} for {job.position}
                    </span>
                  </Typography>
                </Grid>
              )}
             {showOptInBanner && (
                <Box display="flex" justifyContent="flex-end" width="100%">
                  <Box
                    component="div"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #bbdefb',
                      px: 1.5,
                      py: 0.5,
                      boxShadow: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Don't miss a response from this recruiter —{' '}
                    </Typography>

                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setShowOptInBanner(false);
                        handleOpenNotificationsDialog();
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: 0,
                        fontSize: '0.8rem',
                        minWidth: 'auto',
                        ml: 0.5,
                      }}
                    >
                      Opt-In to SMS
                    </Button>

                    <IconButton
                      size="small"
                      aria-label="dismiss"
                      onClick={() => setShowOptInBanner(false)}
                      sx={{
                        p: 0.3,
                        color: 'text.secondary',
                        ml: 0.8,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* { sender.role == "Jobseeker" &&
                !sender.termsAgreed &&
                <Grid
                  item
                  container
                  ref={componentRef}
                  // className={classes.innerChatBody}
                  >
                    <TermsOfServiceNotification
                      company={job.company}
                      sender={sender}
                    />
                </Grid>
              } */}

              <ChatList
                channel={updatedChannel}
                channelArn={channelArn || null}
                channelName={channelName}
                senderUserArn={loggedInUserArn}
                isEndedConversation={isEndedConversation}
                userType={sender.role}
                onLoadMessages={handleLoadMessages}
                connections={updatedConnections}
                platform='mobile'
                inputFocused={inputFocused}
                channelMetadata={channel.metadata ? JSON.parse(channel.metadata) : null}
                timezone={selectedTimezone}
                endOrReenableActionDoer={endOrReenableActionDoer}
                receiver={receiver}
                job={job}
                sender={sender}
                hasReadLastMessage={hasReadLastMessage}
                setHasReadLastMessage={setHasReadLastMessage}
                initialNextToken={initialNextToken}
              />

            </div>
            <ChatInput
              onFocus={handleInputFocusChange}
              connections={connections}
              channel={{
                channelArn: channelArn || null,
                channelName: channelName,
                metadata: channel.metadata,
                isEndedConversation: isEndedConversation
              }}
              updatedChannel={updatedChannel}
              job={job}
              receiver={{
                info: {
                  first_name: receiver.first_name,
                  last_name: receiver.last_name,
                  email: receiver.email,
                  mobile: receiver.mobile
                },
                externalUserId: receiver.external_user_id,
                role: receiver.role,
                userArn: receiver.userArn
              }}
              sender={{
                info: {
                  first_name: sender.first_name,
                  last_name: sender.last_name,
                  email: sender.email,
                  mobile: sender.mobile || null
                },
                externalUserId: sender.external_user_id,
                role: sender.role,
                userArn: sender.userArn
              }}
              onLoadMessages={handleLoadMessages}
              platform='mobile'
              adminID={adminID}
              endOrReenableActionDoer={endOrReenableActionDoer}
              hasReadLastMessage={hasReadLastMessage}
              setHasReadLastMessage={setHasReadLastMessage}
              isLastMessageSender={isLastMessageSender}
              channelMetadata={channelMetadata}
            />
          </Grid>
          </>
          ) : (
            <>
            {/* {sender.role !== "Jobseeker" && */}
              {/* <TermsOfService
                openPopIn={true}
                handleResponse={handleTermsAction}
                token={token}
              /> */}
              {/* } */}
            </>
          )}
        </Grid>

        <Timezone
          openPopIn={isTimezoneDialogOpen} 
          onChange={handleTimezoneChange}
          onClose={handleCloseTimezoneDialog}
          messagingUserId={sender.userArn}
          initialValue={sender?.timezone}
        />

        
        <Notifications
          openPopIn={isNotificationsDialogOpen} 
          onChange={handleOpenNotificationsDialog}
          onClose={handleCloseNotificationsDialog}
          messagingUserId={sender.userArn}
          initialValue={sender?.timezone}
        />

      </div>
      </ThemeProvider>
    </>
  ) : null
  // )
}

export default Chat
